import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';

/**
 * The tenant list (slice 17b).
 *
 * Types mirror the wire contract in `api/src/modules/admin/routes.ts` (seam S1)
 * and are never re-declared from the screen's point of view.
 */

export interface AdminTenant {
  id: string;
  name: string;
  status: 'active' | 'past_due' | 'suspended';
  plan: 'track' | 'fleet' | 'pro';
  /** Integer cents USD, or null when no rate is set — NOT zero. */
  monthlyRateCents: number | null;
  vehicleCount: number;
  /** ISO-8601 closure instant, or null for an open account (slice 27). A
   *  non-null value means the customer is offboarded: access is refused (401)
   *  and the row reads as closed. */
  closedAt: string | null;
  /**
   * Slice 11b — the LATEST detection-pause row for this tenant (seam S5), or null
   * when the tenant has never been closed. Close opens a row (`resumedAt` null);
   * reopen stamps `resumedAt` and, once the closed-window trips have been
   * re-detected, `backfilledAt` + `backfillTrips`. Read-only on this screen — it
   * drives the "Detection paused since…" / "Re-detecting…" / "…re-detected: N"
   * line, and makes no retention promise and offers no delete control.
   *
   * 🔴 Optional here: an older API body carries no `detectionPause` key; a missing
   * field is treated as null (no pause line), never fabricated.
   */
  detectionPause?: {
    pausedAt: string;
    resumedAt: string | null;
    backfilledAt: string | null;
    backfillTrips: number | null;
  } | null;
  /**
   * Slice 14d — is Raad Secure switched on for this tenant? On the wire so the
   * switch renders its REAL position rather than optimistic local state. Not a
   * secret: it says whether a customer has a capability, not how it is used. The
   * dispatch gate is per-device and independent — this being true does not make
   * any vehicle blockable. */
  raadSecureEnabled: boolean;
}

export interface AdminTenantList {
  tenants: AdminTenant[];
  /** Summed server-side from the same rows, so it cannot disagree with them. */
  totalMonthlyCents: number;
}

export const tenantsQueryKey = ['admin', 'tenants'] as const;

export function useTenants(): UseQueryResult<AdminTenantList> {
  return useQuery({
    queryKey: tenantsQueryKey,
    queryFn: ({ signal }) => apiJson<AdminTenantList>('/api/admin/tenants', { signal }),
    // A 403 is a decision, not a hiccup — retrying it just delays the answer.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

export interface ImpersonateResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  tenantId: string;
  tenantName: string;
}

/**
 * Start a read-only impersonation session (slice 17c).
 *
 * 🔴 THE TOKEN IS HANDED TO THE CUSTOMER DASHBOARD AND NEVER STORED HERE.
 *
 * It goes to `app.` in the URL fragment (`#`), not the query string, for a
 * specific reason: a fragment is never sent to the server, so it does not land
 * in Caddy's access log, in a Referer header, or in any proxy in between. A
 * query parameter carrying a live credential that reads a customer's fleet
 * would be written to disk on every hop — the same reasoning that keeps the
 * OsmAnd ingest off a published port.
 *
 * It is also not persisted on this origin at all. Ten minutes, one tab, gone.
 */
export async function startImpersonation(tenantId: string): Promise<ImpersonateResponse> {
  return apiJson<ImpersonateResponse>(`/api/admin/impersonate/${tenantId}`, { method: 'POST' });
}
